import React, { Component } from "react";
import axios from "axios";
import Auth from "../../Auth";
import Loading from "../loading";
import { link, downloadFile, numb2 } from "../link.js";
import { ReportLayout, ReportCard, ReportFilters, RoundSelect, ReportEmpty } from "./reportParts";

// Statement figures the backend leaves empty or zero are shown as "-".
const figure = (value) => (value ? numb2(value) : "-");

function StatementTable({ lines }) {
  return (
    <table className="statement-table">
      <tbody>
        {lines.map((line) => (
          <tr key={line.label} className={line.kind}>
            <th scope="row">{line.label}</th>
            <td>{line.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function StatementSection({ title, children }) {
  return (
    <section className="statement-section">
      <h3>{title}</h3>
      {children}
    </section>
  );
}

function Statements({ report }) {
  const incomeStatement = [
    { label: "Income from Loans", value: numb2(report.incomeFromLoans) },
    { label: "Income from Contributions", value: numb2(report.incomeFromContributions) },
    { label: "Other Income", value: numb2(report.incomeFromOthers) },
    { label: "TOTAL INCOME", value: numb2(report.incomeTotal), kind: "subtotal" },
    { label: "Administrative Expenses", value: figure(report.administrativeExpenses) },
    { label: "Finance Expenses", value: figure(report.financeExpenses) },
    { label: "Director Insentive", value: figure(report.directorIncentive) },
    { label: "PROFIT", value: figure(report.profit), kind: "total" },
  ];
  const changeInEquity = [
    { label: "Share Capital", value: figure(report.totalShareValue) },
    { label: "Monthly Reserve", value: figure(report.topaySavings) },
    { label: "Profit at End of the Year", value: figure(report.profit) },
    { label: "TOTAL", value: figure(report.nextTotalValueOfShares), kind: "total" },
    { label: "Next Share value", value: figure(report.nextShareValue), kind: "subtotal" },
  ];
  const fixedAssets = [
    { label: "Investment", value: figure(report.investment) },
    { label: "Other Asset (Capital)", value: report.totalFixedAsset ? numb2(report.assets) : "-" },
    { label: "TOTAL FIXED ASSETS", value: figure(report.totalFixedAsset), kind: "subtotal" },
  ];
  const currentAssets = [
    { label: "Cash balance", value: figure(report.reserves) },
    { label: "Receivable", value: figure(report.receivable) },
    { label: "Others (Unspecified)", value: figure(report.topayUnindentifiedIncome) },
    { label: "TOTAL CURRENT ASSETS", value: figure(report.totalCurrentAsset), kind: "subtotal" },
    { label: "TOTAL ASSETS", value: figure(report.totalAsset), kind: "total" },
  ];
  const equity = [
    { label: "Share Capital", value: figure(report.totalShareValue) },
    { label: "Monthly Reserves", value: figure(report.topaySavings) },
    { label: "Other Reserves", value: figure(report.existingCash) },
    { label: "Profit of Year", value: figure(report.profitOfYear) },
    { label: "TOTAL EQUITY", value: figure(report.totalEquity), kind: "subtotal" },
  ];
  // The backend names these two totals the other way round, so the labels follow the figures the Excel export uses.
  const liability = [
    { label: "Payable", value: figure(report.payable) },
    { label: "Director's Incentive", value: figure(report.directorIncentive) },
    { label: "TOTAL LIABILITIES", value: figure(report.totalEquityAndLiability), kind: "subtotal" },
    { label: "TOTAL EQUITY AND LIABILITY", value: figure(report.totalLiability), kind: "total" },
    { label: "TOTAL EQ & LB - ASSET", value: figure(report.difference), kind: "total" },
  ];

  return (
    <>
      <StatementSection title="1. Income Statement">
        <StatementTable lines={incomeStatement} />
      </StatementSection>
      <StatementSection title="2. Statement of Change in Equity">
        <StatementTable lines={changeInEquity} />
      </StatementSection>
      <StatementSection title="3. Statement of Financial Position">
        <p className="statement-group">Assets · Fixed assets</p>
        <StatementTable lines={fixedAssets} />
        <p className="statement-group">Assets · Current assets</p>
        <StatementTable lines={currentAssets} />
        <p className="statement-group">Equity and liability · Equity</p>
        <StatementTable lines={equity} />
        <p className="statement-group">Equity and liability · Liability</p>
        <StatementTable lines={liability} />
      </StatementSection>
    </>
  );
}

export class Financialstatements extends Component {
  constructor(props) {
    super(props);
    this.state = { rounds: [], report: null, roundid: "", loading: true, unavailable: "" };
    this.mounted = false;
    this.requestId = 0;
  }

  componentDidMount() {
    this.mounted = true;
    this.getRounds();
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  getRounds = () => {
    fetch(link() + `&do=getshort&ra=round&ft=areclist&elmt=roundStatus&id=Active`, {
      method: "post",
      headers: { "Content-Type": "application/json" },
    })
      .then((response) => response.json())
      .then((response) => {
        if (!this.mounted) return;
        this.setState({ rounds: Array.isArray(response) ? response : [] });
        this.getReport(this.state.roundid, false);
      })
      .catch((err) => {
        console.log("Error: ", err);
        Auth.handleHttpCodes(err, "financialstatements");
        if (this.mounted) this.setState({ loading: false });
      });
  };

  requestBody = (roundid, force) => {
    const body = {
      datefrom: "",
      dateTo: "",
      userid: "",
      memberid: "",
      roundid,
      roundname: "",
      name: "",
      bank: "",
      termstartdate: "",
      termduedate: "",
      status: "",
    };
    if (force) body.forcecreate = true;
    return body;
  };

  // Used when the company has no current round: the current round of the list, else its first round.
  defaultRoundId = () => {
    const { rounds } = this.state;
    const pick = rounds.find((r) => r.current === true || r.current === "true") || rounds[0];
    return pick ? pick.roundID : "";
  };

  getReport = (roundid, force) => {
    const request = ++this.requestId;
    this.setState({ loading: true });
    localStorage.setItem("currentView", "income");
    axios
      .post(link() + `&do=get&ra=report&rec=finstat`, JSON.stringify(this.requestBody(roundid, force)), {
        headers: { "Content-Type": "application/json" },
        timeout: 300000,
      })
      .then((res) => {
        if (!this.mounted || request !== this.requestId) return;
        const report = res.data && typeof res.data === "object" ? res.data : {};
        if (report.available === false) {
          const fallback = this.defaultRoundId();
          if (fallback && fallback !== roundid) {
            this.setState({ roundid: fallback });
            this.getReport(fallback, false);
            return;
          }
          this.setState({ report: null, unavailable: report.message || "", loading: false });
          return;
        }
        this.setState({ report, roundid: report.roundid || roundid, unavailable: "", loading: false });
      })
      .catch((err) => {
        console.log("Error: ", err);
        Auth.handleHttpCodes(err, "financialstatements");
        if (this.mounted && request === this.requestId) this.setState({ loading: false });
      });
  };

  handleRoundSelect = (e) => {
    this.setState({ roundid: e.target.value });
    this.getReport(e.target.value, false);
  };

  handleForceRecreate = () => this.getReport(this.state.roundid, true);

  download = () => {
    downloadFile(
      "POST",
      link() + `&do=get&ra=report&rec=finstat` + `&ufile=getfile`,
      this.requestBody(this.state.roundid, false),
      { "Content-Type": "application/application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
      "FinancialStatements.xlsx"
    );
  };

  render() {
    const { rounds, report, roundid, loading, unavailable } = this.state;
    const hasReport = report && Object.keys(report).length > 0;
    const runDate = hasReport && report.runDate ? report.runDate.substring(0, 19).replace("T", " ") : "";

    return (
      <ReportLayout>
        <ReportCard
          title="Financial Statements:"
          subtitle={report && report.roundName}
          onDownload={hasReport ? this.download : undefined}
          downloadLabel="Download financial statements"
        >
          <ReportFilters>
            <RoundSelect rounds={rounds} value={roundid} onChange={this.handleRoundSelect} />
          </ReportFilters>

          {loading && (
            <>
              <ReportEmpty>This report can take up to several minutes. Wait until it loads completely. Do not refresh the page.</ReportEmpty>
              <Loading />
            </>
          )}
          {!loading && !hasReport && (
            <ReportEmpty>{unavailable || "The financial statements could not be loaded."}</ReportEmpty>
          )}
          {!loading && hasReport && (
            <div className="statement">
              <h3 className="statement-title">Statement of financial position end of round '{report.roundName}'</h3>
              <p className="statement-meta">
                {runDate && <>This report was run on {runDate} Paris Time. </>}
                To refresh click{" "}
                <button type="button" className="statement-link" onClick={this.handleForceRecreate}>
                  here
                </button>
                .
              </p>
              <Statements report={report} />
            </div>
          )}
        </ReportCard>
      </ReportLayout>
    );
  }
}

export default Financialstatements;
